import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSdk } from '@fondationcss/sdk';
import { useSdk as useCustomSdk } from '@/composables/use-sdk';
import type { InitializationStepStatus } from '@/sdk/model/initialization-status';

export interface WizardStep {
  key: string;
  title: string;
  description: string;
  icon: string;
  route: string;
  completed: boolean;
  optional: boolean;
}

const STEP_DEFINITIONS: Omit<WizardStep, 'completed'>[] = [
  {
    key: 'define-sites',
    title: 'Définir les centres',
    description: 'Créer au moins un centre de formation.',
    icon: 'fas fa-building',
    route: '/gestion/centres',
    optional: false,
  },
  {
    key: 'define-locals',
    title: 'Définir les locaux',
    description: 'Créer au moins un local pour accueillir les formations.',
    icon: 'fas fa-door-open',
    route: '/gestion/locaux-ressources',
    optional: false,
  },
  {
    key: 'define-resources',
    title: 'Définir les ressources',
    description: 'Créer et associer au moins une ressource à un local.',
    icon: 'fas fa-desktop',
    route: '/gestion/locaux-ressources',
    optional: true,
  },
  {
    key: 'activate-programs',
    title: 'Activer les programmes',
    description: 'Activer au moins un programme de formation.',
    icon: 'fas fa-book-open',
    route: '/gestion/programmes',
    optional: false,
  },
  {
    key: 'sequence-modules',
    title: 'Séquencer les compétences',
    description: 'Définir les prérequis pour les compétences de chaque programme actif.',
    icon: 'fas fa-sort-amount-down',
    route: '/gestion/programmes',
    optional: false,
  },
  {
    key: 'assign-module-resources',
    title: 'Assigner ressources/locaux aux compétences',
    description: 'Associer des ressources ou locaux aux compétences d\'un programme actif.',
    icon: 'fas fa-link',
    route: '/gestion/programmes',
    optional: true,
  },
  {
    key: 'create-teachers',
    title: 'Créer des enseignants',
    description: 'Ajouter au moins un enseignant dans le système.',
    icon: 'fas fa-chalkboard-teacher',
    route: '/gestion/enseignants',
    optional: false,
  },
  {
    key: 'assign-teacher-modules',
    title: 'Assigner programmes et compétences aux enseignants',
    description: 'Assigner au moins une compétence à un enseignant.',
    icon: 'fas fa-user-tag',
    route: '/gestion/enseignants',
    optional: false,
  },
  {
    key: 'add-students',
    title: 'Ajouter des étudiants',
    description: 'Créer au moins un étudiant dans le système.',
    icon: 'fas fa-user-graduate',
    route: '/planification/etudiants',
    optional: false,
  },
  {
    key: 'add-frequentation',
    title: 'Ajouter une fréquentation',
    description: 'Inscrire au moins un étudiant à un programme actif.',
    icon: 'fas fa-user-plus',
    route: '/planification/frequentations',
    optional: false,
  },
  {
    key: 'plan-group',
    title: 'Planifier et confirmer un groupe',
    description: 'Créer au moins un groupe de formation et le confirmer.',
    icon: 'fas fa-users',
    route: '/planification/vue-ensemble',
    optional: true,
  },
  {
    key: 'plan-individualized',
    title: 'Planifier un parcours individualisé',
    description: 'Créer au moins un parcours individualisé.',
    icon: 'fas fa-user-friends',
    route: '/planification/individualise',
    optional: true,
  },
  {
    key: 'record-presences',
    title: 'Prendre des présences',
    description: 'Enregistrer au moins une présence dans le système.',
    icon: 'fas fa-calendar-check',
    route: '/enseignement/presences',
    optional: false,
  },
];

const DISMISSED_STORAGE_KEY = 'parcours.wizard.dismissed';

const showStepsDialog = ref(false);
const dismissed = ref(readDismissedFlag());

function readDismissedFlag(): boolean {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function dismissWizard() {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
  } catch {
    // Ignore storage errors — dismissal is a convenience, not critical
  }
  dismissed.value = true;
  showStepsDialog.value = false;
}

export function resetWizardDismissal() {
  try {
    localStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
  dismissed.value = false;
  showStepsDialog.value = true;
}

export function useInitializationWizard() {
  const coreSdk = useSdk();
  const customSdk = useCustomSdk();
  const router = useRouter();

  const steps = ref<WizardStep[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const showWelcomeDialog = ref(false);

  const isAdmin = computed(() => {
    const roles = coreSdk.me.value?.roles || [];
    return roles.includes('Parcours.LearningModule.Administrateur');
  });

  const completedCount = computed(() => steps.value.filter(s => s.completed).length);
  const requiredCount = computed(() => steps.value.filter(s => !s.optional).length);
  const requiredCompletedCount = computed(() => steps.value.filter(s => !s.optional && s.completed).length);
  const totalCount = computed(() => steps.value.length);
  const allCompleted = computed(() => requiredCompletedCount.value === requiredCount.value);
  const anyCompleted = computed(() => completedCount.value > 0);
  const showFab = computed(() => isAdmin.value && loaded.value && !dismissed.value);

  function mergeStatus(statusSteps: InitializationStepStatus[]) {
    const statusMap = new Map(statusSteps.map(s => [s.key, s]));
    steps.value = STEP_DEFINITIONS.map(def => ({
      ...def,
      completed: statusMap.get(def.key)?.completed ?? false,
    }));
  }

  async function loadStatus(options?: {
    markLoaded?: boolean;
    showWelcomeWhenEmpty?: boolean;
  }) {
    if (!isAdmin.value) return;
    loading.value = true;
    try {
      const response = await customSdk.getInitializationStatus();
      mergeStatus(response.steps);

      if (options?.markLoaded) {
        loaded.value = true;
      }

      // Show welcome dialog only if no step has been completed yet
      if (options?.showWelcomeWhenEmpty && !anyCompleted.value) {
        showWelcomeDialog.value = true;
      }
    } catch {
      // Silently fail — wizard is non-critical
    } finally {
      loading.value = false;
    }
  }

  async function fetchStatus() {
    await loadStatus({ markLoaded: true, showWelcomeWhenEmpty: true });
  }

  async function refreshStatus() {
    await loadStatus();
  }

  function openStepsDialog() {
    showStepsDialog.value = true;
    refreshStatus();
  }

  function closeStepsDialog() {
    showStepsDialog.value = false;
  }

  function startConfiguration() {
    showWelcomeDialog.value = false;
    openStepsDialog();
  }

  function navigateToStep(step: WizardStep) {
    showStepsDialog.value = false;
    router.push(step.route);
  }

  // Watch for isAdmin to become true (sdk.me loads asynchronously after auth)
  watch(isAdmin, (admin) => {
    if (admin && !loaded.value) {
      fetchStatus();
    }
  }, { immediate: true });

  return {
    steps,
    loading,
    loaded,
    isAdmin,
    completedCount,
    requiredCount,
    requiredCompletedCount,
    totalCount,
    allCompleted,
    anyCompleted,
    showFab,
    showWelcomeDialog,
    showStepsDialog,
    dismissed,
    openStepsDialog,
    closeStepsDialog,
    startConfiguration,
    navigateToStep,
    refreshStatus,
    dismissWizard,
  };
}
